import { appLog } from 'utils/logger';

const log = appLog.extend('event-emitter');

type EventName = string;
type EventPayload = unknown;
type Type = { type: EventName; data?: EventPayload };

interface EventContext {
	[key: string]: unknown;
}

// Utilities bound to a union T
type DataOf<T extends Type, E extends T['type']> = Extract<T, { type: E }>['data'];
type EventOf<T extends Type, E extends T['type']> = {
	type: E;
	data: DataOf<T, E>;
	context: EventContext;
};

type ListenerOf<T extends Type, E extends T['type']> = (event: EventOf<T, E>) => void;

const CANCEL = Symbol('event-cancel');

type InterceptorResult<T extends Type, E extends T['type']> = { data?: DataOf<T, E> } | typeof CANCEL | void;

type MaybePromise<T> = T | Promise<T>;

type InterceptorOf<T extends Type, E extends T['type']> = (
	event: EventOf<T, E>
) => MaybePromise<InterceptorResult<T, E>>;

type ListenerMap<T extends Type> = { [K in T['type']]?: Array<ListenerOf<T, K>> };
type InterceptorMap<T extends Type> = { [K in T['type']]?: Array<InterceptorOf<T, K>> };

const isImmutable = true;

interface EventEmitterOptions {
	onInterceptorStateChange?: (isPending: boolean) => void;
	context?: EventContext;
}

export class EventEmitter<T extends Type> {
	static CANCEL = CANCEL;
	private listeners: ListenerMap<T> = {};
	private interceptors: InterceptorMap<T> = {};
	private pendingInterceptors = 0;
	private options: EventEmitterOptions;

	constructor(options: EventEmitterOptions = {}) {
		this.options = options;
	}

	addListener = <E extends T['type']>(event: E, listener: ListenerOf<T, E>) => {
		(this.listeners[event] ||= []).push(listener as ListenerOf<T, typeof event>);
		return () => this.removeListener(event, listener);
	};

	// Convenience API
	on = this.addListener;

	removeListener = <E extends T['type']>(event: E, listener: ListenerOf<T, E>) => {
		const arr = this.listeners[event];
		if (!arr) return;
		const i = arr.indexOf(listener as ListenerOf<T, typeof event>);
		if (i >= 0) arr.splice(i, 1);
	};

	// Convenience API
	off = this.removeListener;

	once = <E extends T['type']>(event: E, listener: ListenerOf<T, E>) => {
		const onceWrapper: ListenerOf<T, E> = ev => {
			this.off(event, onceWrapper);
			listener(ev);
		};
		return this.on(event, onceWrapper);
	};

	intercept = <E extends T['type']>(event: E, interceptor: InterceptorOf<T, E>) => {
		(this.interceptors[event] ||= []).push(interceptor as InterceptorOf<T, typeof event>);
		return () => {
			const arr = this.interceptors[event];
			if (!arr) return;
			const i = arr.indexOf(interceptor as InterceptorOf<T, typeof event>);
			if (i >= 0) arr.splice(i, 1);
		};
	};

	// Await interceptors; listeners stay sync
	emit = async <E extends T['type']>(event: E, data?: DataOf<T, E>): Promise<void> => {
		const ctx: EventOf<T, E> = {
			type: event,
			data: cloneData(data as DataOf<T, E>),
			context: this.options.context || {}, // Don't clone context - it contains functions
		};

		if (isImmutable) {
			deepFreeze(ctx);
		}

		const interceptors = [...(this.interceptors[event] || [])];
		const totalInterceptors = interceptors.length;
		const hasInterceptors = totalInterceptors > 0;

		if (hasInterceptors) {
			if (this.pendingInterceptors === 0) {
				this.options.onInterceptorStateChange?.(true);
			}

			this.pendingInterceptors += totalInterceptors;
			log('Pending interceptors:', this.pendingInterceptors);
		}

		for (const fn of interceptors) {
			try {
				// eslint-disable-next-line no-await-in-loop
				const res = await fn(ctx);
				this.pendingInterceptors -= 1;
				log('Interceptor done, remaining:', this.pendingInterceptors);

				if (res === CANCEL) {
					this.pendingInterceptors = 0;
					this.options.onInterceptorStateChange?.(false);
					return;
				}

				if (!isImmutable) {
					if (res && typeof res === 'object' && 'data' in res) {
						ctx.data = res.data as DataOf<T, E>;
					}
				}
			} catch (error) {
				this.pendingInterceptors -= 1;
				log('Interceptor done, remaining:', this.pendingInterceptors);
				throw error;
			}
		}

		if (hasInterceptors && this.pendingInterceptors === 0) {
			this.options.onInterceptorStateChange?.(false);
		}

		for (const listener of [...(this.listeners[event] || [])]) {
			listener(ctx as any);
		}
	};

	destroy = () => {
		this.listeners = {};
		this.interceptors = {};
	};
}

function cloneData<T>(data: T): T {
	if (typeof structuredClone !== 'undefined') {
		return structuredClone(data);
	}
	return JSON.parse(JSON.stringify(data));
}

function deepFreeze<T extends object>(obj: T): Readonly<T> {
	const propNames = Object.getOwnPropertyNames(obj);
	propNames.forEach(name => {
		const prop = obj[name];
		// Don't freeze functions, only objects
		if (typeof prop === 'object' && prop !== null && typeof prop !== 'function') {
			deepFreeze(prop);
		}
	});
	return Object.freeze(obj);
}

/*
type MyEvents =
	| { type: "NAVIGATION"; data: { direction: "forward" | "back" } }
	| { type: "ANSWER_SUBMIT"; data: { value: string } };

const emitter = new EventEmitter<MyEvents>();

// interceptor
emitter.intercept("NAVIGATION", async (e) => {
	console.log("Show ad before navigation...");
	await new Promise(res => setTimeout(res, 2000));
	console.log("Ad finished!");
	// return CANCEL; // to block
	return { data: { ...e.data, direction: "forward" } }; // can mutate
});

// listener
emitter.on("NAVIGATION", (e) => {
	console.log("Final NAVIGATION event →", e.data);
});

(async () => {
	await emitter.emit("NAVIGATION", { direction: "forward" });
})();
*/
